/** Per-action custom UI for the preflight checklist item detail.
 *
 * The preflight tab is the control center for tour-operator configuration: each
 * action should show its current value(s) and let ops change them. Generic scalar
 * values render via the inline field editor (`config_fields`); richer shapes get a
 * bespoke control here, dispatched on the item's `action` code and rendered in the
 * item detail alongside the field editor.
 *
 * Each case is a self-contained component that owns its own mutation + snackbar and
 * calls `onChanged()` (query invalidation) on success. A control that offers the
 * item's `execute/` click must also be listed in `bespokeExecute.ts`, so the action
 * bar's generic run-style button stands down instead of duplicating it — and a
 * control whose button would be nothing BUT that call should not have one at all. Returns null for actions with
 * no custom UI — the generic field editor + resources links still render.
 *
 * Cases are added by each action's PR, e.g.:
 *   case 'LOGO_UPLOAD':   return <LogoUploadControl {...props} />;
 *   case 'SUPPORT_CONTACTS': return <SupportContactsControl {...props} />;
 */
import { Alert, Box } from '@mui/material';

import type { PreflightItem } from '@/api/preflight';

import { AccessGroupsSetupControl } from './AccessGroupsSetupControl';
import { AppConfigJsonControl } from './AppConfigJsonControl';
import { AppStoreLinksDisplay } from './AppStoreLinksDisplay';
import { AuthMethodsControl } from './AuthMethodsControl';
import { BookingIntegrationsControl } from './BookingIntegrationsControl';
import { BranchConfigEditor } from './BranchioAppSetupControl';
import { ChannelEnableGate } from './ChannelEnableGate';
import { DnsCnameControl } from './DnsCnameControl';
import { EmployeeRolesControl } from './EmployeeRolesControl';
import { FirebaseFcmCredentialControl } from './FirebaseFcmCredentialControl';
import { FirebaseProjectSetupControl } from './FirebaseProjectSetupControl';
import { FleetSystemSetupControl } from './FleetSystemSetupControl';
import { FleetVehiclesRegisterControl } from './FleetVehiclesRegisterControl';
import { GmapsSdkKeySetupControl } from './GmapsSdkKeySetupControl';
import { GmapsSha1Control } from './GmapsSha1Control';
import { GraphicalAssetsControl } from './GraphicalAssetsControl';
import { JiraRequestControl } from './JiraRequestControl';
import { LocationPermissionsDisplay } from './LocationPermissionsDisplay';
import { LogoUploadControl } from './LogoUploadControl';
import { NotificationSubscriptionsDisplay } from './NotificationSubscriptionsDisplay';
import { PreflightDetailDisplay } from './PreflightDetailDisplay';
import { RestechDetailDisplay } from './RestechDetailDisplay';
import { ReviewGuestControl } from './ReviewGuestDisplay';
import { SupportContactsControl } from './SupportContactsControl';
import { TagCategoriesDisplay } from './TagCategoriesDisplay';
import { TeamUsersControl } from './TeamUsersControl';
import { TwilioApnCredentialControl } from './TwilioApnCredentialControl';
import { TwilioConversationsControl } from './TwilioConversationsControl';
import { TwilioLimitsControl } from './TwilioLimitsControl';
import { TwilioSmsNumberControl } from './TwilioSmsNumberControl';
import { TwilioWebhookControl } from './TwilioWebhookControl';
import { TwilioWhatsappControl } from './TwilioWhatsappControl';

/** Explicit-manual-action note. Actions flagged `detail.manual_action` have no backend
 *  automation by design: ops do the work in the linked dashboard(s) (rendered just below
 *  this, under "Dashboards") and then mark the item done. Renders nothing otherwise. */
function ManualActionNote({ item }: { item: PreflightItem }) {
  if (!item.detail?.manual_action) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Alert severity="info">
        This step is done by hand — there is no automation for it. Complete it in the linked dashboard(s)
        below, then mark it done.
      </Alert>
    </Box>
  );
}

export interface ActionUIProps {
  item: PreflightItem;
  topId: number;
  /** The operator's tenant code — some controls open shared dialogs that need it (e.g. fleet). */
  toCode: string;
  canManage: boolean;
  /** Invalidate the preflight queries after a successful mutation. */
  onChanged: () => void;
}

export function ActionCustomUI(props: ActionUIProps) {
  switch (props.item.action) {
    case 'LOGO_UPLOAD':
      return <LogoUploadControl {...props} />;
    case 'SUPPORT_CONTACTS':
      return <SupportContactsControl {...props} />;
    case 'TAG_CATEGORIES':
    case 'LOCATION_TAG_CATEGORIES':
      return <TagCategoriesDisplay {...props} />;
    case 'LOCATION_PERMISSIONS':
      return <LocationPermissionsDisplay item={props.item} />;
    case 'FLEET_SYSTEM_SETUP':
      return <FleetSystemSetupControl {...props} />;
    case 'FLEET_VEHICLES_REGISTER':
      return <FleetVehiclesRegisterControl {...props} />;
    case 'DNS_CNAME_RECORD':
      return <DnsCnameControl {...props} />;
    case 'REVIEW_GUEST_SETUP':
      return <ReviewGuestControl {...props} />;
    case 'EMPLOYEE_ROLES':
      return <EmployeeRolesControl item={props.item} />;
    case 'APP_STORE_LINKS_FOLLOWUP':
      return <AppStoreLinksDisplay item={props.item} />;
    case 'ACCESS_GROUPS_SETUP':
      return <AccessGroupsSetupControl {...props} />;
    case 'AUTH_METHODS_CONFIG':
      return <AuthMethodsControl {...props} />;
    case 'APP_CONFIG_JSON':
      return <AppConfigJsonControl {...props} />;
    case 'BRANCHIO_APP_SETUP':
      return <BranchConfigEditor {...props} showSubdomain />;
    case 'BRANCHIO_ANDROID_FOLLOWUP':
      return (
        <BranchConfigEditor
          {...props}
          editKeys={['android_url', 'android_uri_scheme']}
          readKeys={['sha256_cert_fingerprints', 'android_package_name']}
          completionKeys={['android_redirect_set', 'android_fingerprint_set']}
        />
      );
    case 'BRANCHIO_IOS_FOLLOWUP':
      return (
        <BranchConfigEditor
          {...props}
          editKeys={['ios_url', 'ios_uri_scheme']}
          readKeys={['ios_team_id', 'ios_bundle_id']}
          completionKeys={['ios_redirect_set', 'ios_team_id_set']}
        />
      );
    case 'TEAM_USERS':
      return <TeamUsersControl {...props} />;
    case 'FIREBASE_PROJECT_SETUP':
      return <FirebaseProjectSetupControl {...props} />;
    case 'GMAPS_SDK_KEY_SETUP':
      return <GmapsSdkKeySetupControl {...props} />;
    case 'GMAPS_SHA1_FOLLOWUP':
      return <GmapsSha1Control {...props} />;
    case 'TWILIO_CONVERSATIONS_SERVICE':
      return <TwilioConversationsControl {...props} />;
    case 'TWILIO_WEBHOOK_PUSH_CONFIG':
      return <TwilioWebhookControl {...props} />;
    case 'TWILIO_LIMITS_ROLES':
      return <TwilioLimitsControl {...props} />;
    case 'TWILIO_SMS_NUMBER':
      return (
        <ChannelEnableGate {...props}>
          <TwilioSmsNumberControl {...props} />
        </ChannelEnableGate>
      );
    case 'TWILIO_WHATSAPP_SENDER':
      return (
        <ChannelEnableGate {...props}>
          <TwilioWhatsappControl {...props} />
        </ChannelEnableGate>
      );
    case 'TWILIO_APN_CREDENTIAL':
      return (
        <ChannelEnableGate {...props}>
          <TwilioApnCredentialControl {...props} />
        </ChannelEnableGate>
      );
    case 'FIREBASE_FCM_CREDENTIAL':
      return (
        <ChannelEnableGate {...props}>
          <FirebaseFcmCredentialControl {...props} />
        </ChannelEnableGate>
      );
    case 'GRAPHICAL_ASSETS':
      return <GraphicalAssetsControl {...props} />;
    case 'APP_BUILD_ANDROID':
    case 'APP_BUILD_IOS':
    case 'PUSH_CERT_REQUEST':
      return <JiraRequestControl {...props} />;
    case 'RESTECH_PROVIDER_CONFIG':
      return <BookingIntegrationsControl {...props} />;
    case 'NOTIFICATION_SUBSCRIPTIONS':
      return <NotificationSubscriptionsDisplay item={props.item} topId={props.topId} />;
    case 'RESTECH_SOURCE_CONFIG':
    case 'IMPORT_SOURCE_PERMISSIONS':
    case 'IMPORT_ACCESS_TOKEN_CHECK':
      return <RestechDetailDisplay {...props} />;
    // Further per-action controls are registered here by their respective PRs.
    default:
      // Actions with no bespoke control: the manual-action note when flagged, plus
      // a generic read-out of `detail()` — which used to reach the UI only through
      // this switch, so an action with rich read-state and no component here showed
      // nothing at all. The generic field editor + resource links still render above.
      return (
        <>
          <ManualActionNote item={props.item} />
          <PreflightDetailDisplay item={props.item} />
        </>
      );
  }
}
